import { Paper, Stack, IconButton, Chip, Typography } from '@mui/material';
import type {
  AnonymousCustomersReport,
  CommitmentUsageReport,
  ContractsReport,
  CustomerRevenueReport,
  CustomersReport,
  FailedInvoiceDeliveriesReport,
  RecurringRevenueReport,
  MonthlyClosureReport,
  ProductsUsageReport,
  UnchargedUsageReport,
} from '@vy/types';
import { vyDate, isArray, isNotNullOrUndefined, isNullOrUndefined, isToday } from '@vy/utils';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { ArrowBack } from '@mui/icons-material';

type Reports =
  | CommitmentUsageReport
  | UnchargedUsageReport
  | ProductsUsageReport
  | ContractsReport
  | RecurringRevenueReport
  | CustomerRevenueReport
  | AnonymousCustomersReport
  | MonthlyClosureReport
  | CustomersReport
  | FailedInvoiceDeliveriesReport;

type ReportHeaderProps = {
  reportData: Reports;
  creationDate?: Date;
  startDate?: Date;
  endDate?: Date;
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  reportData,
  creationDate,
  startDate,
  endDate,
}) => {
  const navigate = useVyNavigate();

  const getFormattedDate = () => {
    if (isNotNullOrUndefined(startDate) && isNotNullOrUndefined(endDate)) {
      return `${formatHeaderDateWithoutTime(startDate)} — ${formatHeaderDateWithoutTime(endDate)}`;
    }
    if (isNullOrUndefined(startDate) && isNotNullOrUndefined(endDate)) {
      return `As of ${formatHeaderDate(endDate)}`;
    }
    if (isNotNullOrUndefined(creationDate)) {
      return `As of ${formatHeaderDate(creationDate)}`;
    }

    return '';
  };

  return (
    <Paper
      sx={{
        padding: 2,
        borderRadius: '8px',
        position: 'relative',
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between"
        }}>
        <Stack direction="row" spacing={1.5} sx={{
          alignItems: "center"
        }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "24px",
              color: "#0D1B27DE"
            }}>
            {getFormattedDate()}
          </Typography>
        </Stack>
        {isArray(reportData) && <Chip label={`${reportData.length} items`} />}
      </Stack>
    </Paper>
  );
};

export function formatHeaderDate(date: Date) {
  if (isToday(date)) {
    return 'today' + vyDate(date).format(' h:mm a');
  }

  return vyDate(date).format('DD MMMM YY HH:mm');
}

export function formatHeaderDateWithoutTime(date: Date) {
  return vyDate(date).format('DD MMMM YYYY');
}
